import { AxiosPromise, AxiosStatic } from "axios";
import {
  FileInterface,
  KycCountryList,
  KycDocumentType,
  KycEvidenceUploadArgs,
  KycEvidenceUploadOptions,
  KycIdentityDetailsBody,
  KycProfile,
  KycRequestAnswerBody,
  KycRequirements,
  KycUploadResult,
} from "../types";

export const uploadKYCDocument =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (
    file: File,
    documentType: KycDocumentType
  ): AxiosPromise<FileInterface> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("documentType", documentType);

    return await appAxios.post(`${serverURL}/kyc/document`, formData, {
      withCredentials: true,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

/**
 * @deprecated Use deleteKycDocument instead. This helper targets the legacy
 * Yoti endpoint and will be removed once the migration is complete.
 */
export const deleteKYCDocument =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (documentId: string): AxiosPromise<void> =>
    await appAxios.delete(`${serverURL}/yoti/document/${documentId}`, {
      withCredentials: true,
    });

export const deleteKycDocument =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (documentId: string): AxiosPromise<KycRequirements | undefined> =>
    await appAxios.delete(`${serverURL}/kyc/document/${documentId}`, {
      withCredentials: true,
    });

export const putKycDescriptions =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: {
    sourceOfFundDescription?: string;
    proofOfAddressDescription?: string;
  }): AxiosPromise<KycProfile> => {
    return await appAxios.put(`${serverURL}/kyc`, data, {
      withCredentials: true,
    });
  };

/**
 * @deprecated Use putKycDescriptions instead. This function will be removed in a future version.
 * Legacy function maintained for backward compatibility.
 */
export const postSourceOfFundDescription =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (sourceOfFundDescription: string): AxiosPromise<KycProfile> => {
    return await appAxios.post(
      `${serverURL}/yoti/sourceOfFund`,
      {
        sourceOfFundDescription,
      },
      {
        withCredentials: true,
      }
    );
  };

export const markKycSubmitted =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<KycRequirements | KycProfile | undefined> => {
    return await appAxios.post(
      `${serverURL}/kyc/mark-submitted`,
      {},
      {
        withCredentials: true,
      }
    );
  };

export const getKycRequirements =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<KycRequirements> =>
    await appAxios.get(`${serverURL}/kyc/requirements`, {
      withCredentials: true,
    });

export const getKycCountryList =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<KycCountryList> =>
    await appAxios.get(`${serverURL}/kyc/countries`, {
      withCredentials: true,
    });

export const uploadKycEvidence =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (
    file: File,
    args: KycEvidenceUploadArgs,
    options: KycEvidenceUploadOptions = {}
  ): AxiosPromise<KycUploadResult> => {
    const { onUploadProgress, signal, timeout } = options;
    const formData = new FormData();
    formData.append("file", file);
    if (args.documentType) formData.append("documentType", args.documentType);
    formData.append("clientUploadId", args.clientUploadId);
    formData.append("category", args.category);
    formData.append("requirementId", args.requirementId);

    return await appAxios.post(`${serverURL}/kyc/document`, formData, {
      withCredentials: true,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      ...(onUploadProgress && { onUploadProgress }),
      ...(signal && { signal }),
      ...(timeout !== undefined && { timeout }),
    });
  };

export const answerKycRequest =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (
    requestId: string,
    body: KycRequestAnswerBody
  ): AxiosPromise<KycRequirements> =>
    await appAxios.post(
      `${serverURL}/kyc/requests/${encodeURIComponent(requestId)}/answer`,
      body,
      { withCredentials: true }
    );

export const correctKycIdentityDetails =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (body: KycIdentityDetailsBody): AxiosPromise<KycRequirements> =>
    await appAxios.put(`${serverURL}/kyc/identity-details`, body, {
      withCredentials: true,
    });
